import { useEffect, useRef } from 'react';
import { fihristBul } from '../kilavuz/fihrist';
import { useAuth } from '../lib/auth';

/**
 * «?» İPUCU — bir rakamın, sütunun ya da alanın yanına konur:
 *   <Ipucu id="ciro" />
 *
 * Üzerine gelince, odaklanınca ya da dokununca fihristteki maddenin kısa
 * açıklaması balonda açılır. Balonu ve sağ tuş menüsünü TEK bir katman çizer
 * (`FihristKatmani`, kabukta bir kez durur); bu bileşen yalnız "aç / kapat"
 * der. Böylece balon tablo başlıklarının ve kaydırılan kutuların altında kalmaz.
 *
 * Bağlandığı öğeyi (sütun başlığı, kart) `data-fihrist` ile işaretler; o öğeye
 * sağ tıklamak "Bu nedir?" menüsünü açar.
 *
 * `id` fihristte yoksa hiçbir şey çizilmez — `scripts/fihrist-denetle.mjs`
 * bunu derlemede yakalar.
 */

export type BalonIstegi = {
  id: string;
  /** Balonun yanında açılacağı yer (ekran koordinatı). */
  kutu: { sol: number; ust: number; sag: number; alt: number };
  /** O kullanıma özel ek not (kartın kendi açıklaması). */
  not?: string;
  /** Tıklamayla açıldı: fare çekilince kapanmaz. */
  sabit: boolean;
  /** Balonu açan öğe — kapanınca odak buraya döner. */
  kaynak?: HTMLElement | null;
  /** Klavyeyle açıldı: odak balondaki bağlantıya geçer. */
  odak?: boolean;
};
type Olay = { tur: 'ac'; istek: BalonIstegi } | { tur: 'kapat'; hemen: boolean } | { tur: 'bekle' };

let dinleyici: ((o: Olay) => void) | null = null;
/** Katman kendini buraya yazar; katman yokken (kabuğun dışındaki sayfa) çağrılar boşa düşer. */
export function fihristKatmaniBagla(fn: ((o: Olay) => void) | null) { dinleyici = fn; }
export const balonAc = (istek: BalonIstegi) => dinleyici?.({ tur: 'ac', istek });
export const balonKapat = (hemen = false) => dinleyici?.({ tur: 'kapat', hemen });
/** Fare balonun üstüne geldi: kapanmayı ertele. */
export const balonBekle = () => dinleyici?.({ tur: 'bekle' });

export const kutuAl = (el: Element) => {
  const r = el.getBoundingClientRect();
  return { sol: r.left, ust: r.top, sag: r.right, alt: r.bottom };
};

export default function Ipucu({ id, not }: { id: string; not?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const { can } = useAuth();
  const ham = fihristBul(id);
  // Rolün görmediği konunun ipucu da çizilmez (kılavuzla aynı kural).
  const madde = ham && (!ham.cap || can(ham.cap)) ? ham : undefined;

  // Bağlandığı sütun başlığını / kartı sağ tuş için işaretle (zaten işaretliyse dokunma).
  useEffect(() => {
    const el = ref.current;
    if (!el || !madde) return;
    const kap = el.closest('th, .ccd-kpi, .ccd-stat') ?? el.parentElement;
    if (kap && !kap.hasAttribute('data-fihrist')) kap.setAttribute('data-fihrist', id);
  }, [id, madde]);

  if (!madde) return null;

  const ac = (sabit: boolean, odak = false) => {
    if (ref.current) balonAc({ id, kutu: kutuAl(ref.current), not, sabit, kaynak: ref.current, odak });
  };

  return (
    <span
      ref={ref}
      className="ccd-fipucu"
      role="button"
      tabIndex={0}
      aria-haspopup="dialog"
      aria-label={`${madde.baslik} nedir: ${madde.kisa}`}
      data-fihrist-dugme={id}
      onMouseEnter={() => ac(false)}
      onMouseLeave={() => balonKapat()}
      onFocus={() => ac(false)}
      onBlur={() => balonKapat()}
      // Kart ya da satır tıklanabilir olabilir: tıklama oraya geçmesin.
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); ac(true); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); ac(true, true); }
        else if (e.key === 'Escape') balonKapat(true);
      }}
    >
      ?
    </span>
  );
}
